import { useEffect, useRef } from "react";
import { MediaAttachmentEntity } from "@/indexedDB/types";
import { processMediaAttachment } from "@/middleware/attachments";
import { isImageType } from "@/utils/attachmentType";
import { MAX_CACHEABLE_ENTRY_BYTES } from "@/middleware/mediaMemoryCache";

type Options = {
  /** Only prefetch while the viewer is open. */
  enabled: boolean;
  index: number;
  attachments: MediaAttachmentEntity[];
};

/** At most this many neighbour warms in flight, so they can't starve the item
 *  actually on screen (which fetches outside this hook, uncapped by design). */
const MAX_CONCURRENT_PREFETCH = 2;

/**
 * Which neighbours to warm, given the direction of travel. Symmetric ±1 spends
 * half its budget behind the user; once a direction is known we look TWO ahead
 * and only one back, so the extra warm lands on the slide they're statistically
 * about to reach. Direction is unknown on open → plain ±1 (which also keeps the
 * open burst small).
 */
export function prefetchWindow(index: number, direction: 1 | -1 | 0): number[] {
  if (direction === 1) {
    return [index + 1, index + 2, index - 1];
  }
  if (direction === -1) {
    return [index - 1, index - 2, index + 1];
  }
  return [index + 1, index - 1];
}

/** Prefetch is pointless above the memory cache's per-entry ceiling: the result
 *  is refused by the cache, so it costs full network + decrypt and still leaves
 *  a disk read on arrival. (It also bounded an unlimited cellular pull — images
 *  may be up to 40 MB.) 0/undefined size = unknown → allowed, as before. */
export function isPrefetchableSize(fileSize: number | undefined): boolean {
  return !fileSize || fileSize <= MAX_CACHEABLE_ENTRY_BYTES;
}

/**
 * Warm the cache with neighbouring slides' full-size content so navigating to
 * them is instant. Cache-warming only — no object URLs are created here, so the
 * blob-URL lifecycle stays "one live URL at a time" (the displayed item's).
 * Images only: videos stream on demand and large ones must never be pulled
 * eagerly.
 */
export default function useViewerPrefetch({
  enabled,
  index,
  attachments,
}: Options) {
  // Direction of the last move, so the lookahead follows the user. Reset when
  // the viewer closes so the next open starts neutral (±1).
  const directionRef = useRef<1 | -1 | 0>(0);
  const lastIndexRef = useRef(index);
  if (index !== lastIndexRef.current) {
    // Only TWO REAL positions imply a direction: the persistent mount parks
    // index at -1 while the viewer is closed, and reading the -1→k open
    // transition as a rightward swipe made every fresh open warm k+1, k+2,
    // k-1 instead of the documented neutral ±1.
    if (index >= 0 && lastIndexRef.current >= 0) {
      directionRef.current = index > lastIndexRef.current ? 1 : -1;
    }
    lastIndexRef.current = index;
  }
  if (!enabled) {
    directionRef.current = 0;
    lastIndexRef.current = index;
  }

  // index < 0 = the current item isn't in the nav list (a document, or one
  // momentarily absent). There is no meaningful neighbourhood — warming
  // attachments[0] would be arbitrary.
  const targets = (index < 0 ? [] : prefetchWindow(index, directionRef.current))
    .filter((i) => i >= 0 && i < attachments.length)
    .map((i) => attachments[i])
    .filter(
      (n): n is MediaAttachmentEntity =>
        !!n?.id && isImageType(n.fileType) && isPrefetchableSize(n.fileSize),
    );
  const targetIds = targets.map((n) => n.id).join(",");

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const controller = new AbortController();
    // Run at most MAX_CONCURRENT_PREFETCH at a time, in window order (nearest
    // first), so a wider lookahead never means more simultaneous requests.
    let cursor = 0;
    const pump = async (): Promise<void> => {
      while (cursor < targets.length && !controller.signal.aborted) {
        const neighbor = targets[cursor++];
        await processMediaAttachment({
          chatId: neighbor.chatId,
          messageId: neighbor.messageId,
          messageTimestamp: neighbor.timestamp,
          attachmentMeta: {
            attachmentId: neighbor.id,
            filename: neighbor.filename,
            fileType: neighbor.fileType,
            fileSize: neighbor.fileSize ?? 0,
          },
          getThumbnail: false,
          signal: controller.signal,
        }).catch(() => {
          /* best-effort prefetch */
        });
      }
    };
    for (let i = 0; i < MAX_CONCURRENT_PREFETCH; i++) {
      void pump();
    }
    return () => controller.abort();
    // Keyed on the neighbour IDS (not the `attachments` array): the live query
    // returns a fresh array on every messages-table write, which would
    // otherwise abort + restart the neighbour fetches on each unrelated write
    // while the viewer is open. Same ids ⇒ same fetches, so the objects are
    // safe to read even when their reference changed without a re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, targetIds]);
}
